import HttpClient from "../HttpClient";
import { BookByDishCartResponse } from "../interfaces/BookByDish/BookByDishCartResponse";
import { BookByDishCountUnit } from "../enums/BookByDishCountUnit";

class BookByDishService {
    // Returns the user's active cart, or null when none exists.
    async getCart(userId: number): Promise<BookByDishCartResponse | null> {
        const res = await HttpClient.get(`/centaurus/book-by-dish/user/${userId}/cart`);
        return res?.data ?? null;
    }

    // Add or update a dish in the cart (count = 0 removes it). Returns the updated cart.
    // When houseId is supplied, the backend sets cart.houseId + areaId and re-prices.
    // countUnit PLATE means `count` is plates (bread). The backend then also drops any
    // piece-based bread lines an older customer build left in the cart.
    async upsertCartItem(
        userId: number,
        dishId: number,
        count: number,
        areaId?: number,
        houseId?: number,
        countUnit?: BookByDishCountUnit,
    ): Promise<BookByDishCartResponse | null> {
        const res = await HttpClient.post(`/centaurus/book-by-dish/user/${userId}/cart/items`, {
            dishId,
            count,
            ...(countUnit ? { countUnit } : {}),
            ...(areaId ? { areaId } : {}),
            ...(houseId ? { houseId } : {}),
        });
        return res?.data ?? null;
    }

    // Update cart schedule (date / time / area / house). Changing house/area re-prices items. Returns the updated cart.
    async updateCartSchedule(
        userId: number,
        payload: { date?: string; time?: string; areaId?: number; houseId?: number },
    ): Promise<BookByDishCartResponse> {
        const res = await HttpClient.patch(`/centaurus/book-by-dish/user/${userId}/cart`, payload);
        return res?.data;
    }

    // Remove a single dish from the cart. Returns the updated cart.
    async removeCartItem(userId: number, dishId: number): Promise<BookByDishCartResponse> {
        const res = await HttpClient.delete(`/centaurus/book-by-dish/user/${userId}/cart/items/${dishId}`);
        return res?.data;
    }

    // Delete the whole active cart.
    async deleteCart(userId: number): Promise<void> {
        await HttpClient.delete(`/centaurus/book-by-dish/user/${userId}/cart`);
    }

    // Preview price and time for a dish + count (does not touch the cart).
    // Without countUnit PLATE a bread count is priced as pieces.
    async getDishPriceAndTime(
        dishId: number,
        count: number,
        areaId?: number,
        countUnit?: BookByDishCountUnit,
    ): Promise<{ dishId: number; count: number; price: number; time: number }> {
        const res = await HttpClient.get(`/centaurus/book-by-dish/dishes/${dishId}/price`, {
            params: { count, ...(areaId ? { areaId } : {}), ...(countUnit ? { countUnit } : {}) },
        });
        return res?.data;
    }

    // Create the booking (party) from the customer's active cart.
    async checkout(
        userId: number,
        payload: { houseId: number; couponCode?: string; fullPayment?: boolean },
    ): Promise<any> {
        const res = await HttpClient.post(`/centaurus/book-by-dish/user/${userId}/checkout`, payload);
        return res?.data;
    }

    // Replace the dish set on an existing Book by Dish visit. Returns the updated party.
    // Every item must carry its own line's countUnit: an unchanged plate line sent without
    // PLATE reads as a unit switch and gets re-priced as pieces.
    async editVisit(
        partyId: number,
        payload: { items: { dishId: number; count: number; countUnit?: BookByDishCountUnit }[] },
    ): Promise<any> {
        const res = await HttpClient.patch(`/centaurus/book-by-dish/visit/${partyId}/edit`, payload);
        return res?.data;
    }

    // Reschedule an existing Book by Dish visit.
    async rescheduleVisit(
        partyId: number,
        payload: { date: string; time: string; rescheduleReason?: string; rescheduleSubReason?: string },
    ): Promise<any> {
        const res = await HttpClient.patch(`/centaurus/book-by-dish/visit/${partyId}/reschedule`, payload);
        return res?.data;
    }

    // Admin-only: rewrite the visit charges on an existing Book by Dish visit. The backend
    // re-prices totals, re-evaluates the coupon and re-routes the delta across the orders.
    // The reason, when given, is stored as a supply comment on the visit. Returns the updated party.
    async updateVisitCharges(
        partyId: number,
        payload: { visitCharges: number; reason?: string },
    ): Promise<any> {
        const res = await HttpClient.patch(`/centaurus/book-by-dish/visit/${partyId}/visit-charges`, payload);
        return res?.data;
    }

    // Fetch all pending carts (admin view).
    async fetchAllCarts(limit: number, offset: number, filters: any = {}, sort: { field: string; order: string } = { field: "id", order: "DESC" }): Promise<{ data: any[]; total: number }> {
        const builtFilters: any = { ...filters };
        if (builtFilters.customerPhone) {
            builtFilters.customerPhone =   `${builtFilters.customerPhone}` ;
        }
        const res = await HttpClient.get('/centaurus/book-by-dish/carts', {
            params: { limit, offset, filters: builtFilters, sort },
        });
        return {
            data: res?.data?.data ?? [],
            total: res?.data?.count ?? 0,
        };
    }

    async deleteCartById(cartId: number): Promise<void> {
        await HttpClient.delete(`/centaurus/book-by-dish/carts/${cartId}`);
    }

    // Dishes eligible for Book by Dish, for the add-item picker.
    async getBookByDishDishes(): Promise<any[]> {
        const filters = { forBookByDish: true };
        const res = await HttpClient.get(`/cuisine/dishes`, {
            params: { limit: 1000, offset: 0, filters, sort: {} },
        });
        return res?.data?.data ?? [];
    }
}

export default BookByDishService;
